// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { MapPin } from 'lucide-react'
import { friendlyError } from '../../api/errors'
import { getUserSiteOfCareScope, setUserSiteOfCareScope, type SiteOfCareScopeMode } from '../../api/siteOfCare'

/**
 * D-SOC-3 — "can this account see ALL prescriptions and patients for its NPI, or only facility
 * residents, or only residential patients?" One radio per external account (Provider, Provider
 * Nurse, Facility Staff). Facility grants for the 'facility' choice live in FacilityAccessSection,
 * mounted right below this. Same props as the other access sections so it drops into both
 * user-management surfaces.
 */
export const SCOPE_OPTIONS: { value: SiteOfCareScopeMode; label: string; help: string }[] = [
  { value: 'all', label: 'All prescriptions and patients for this NPI', help: 'The prescriber grant alone decides what this account sees.' },
  { value: 'facility', label: 'Facility residents only', help: 'Only prescriptions whose delivery address is a Site of Care facility. Pick the facilities below — with none picked this account sees nothing.' },
  { value: 'residential', label: 'Residential patients only', help: 'Only prescriptions delivered to a patient’s own home.' },
]

export function scopeRoleSelected(roles: { name: string }[]): boolean {
  return roles.some(r => r.name === 'Provider' || r.name === 'Provider Nurse' || r.name === 'Facility Staff')
}

export default function SiteOfCareScopeSection({ userId, roles, canWrite, onChanged }: {
  userId: string
  roles: { id: string; name: string }[]
  canWrite: boolean
  onChanged: () => void
}) {
  const show = scopeRoleSelected(roles)
  const [scope, setScope] = useState<SiteOfCareScopeMode>('all')
  const [saved, setSaved] = useState<SiteOfCareScopeMode>('all')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    getUserSiteOfCareScope(userId)
      .then(d => { setScope(d.scope); setSaved(d.scope) })
      .catch(e => setErr(friendlyError(e)))
  }, [userId, show])

  if (!show) return null

  const save = async () => {
    setBusy(true); setErr(null); setNote(null)
    try {
      await setUserSiteOfCareScope(userId, scope)
      setSaved(scope); setNote('Saved. Takes effect on the user’s next page load.'); onChanged()
    } catch (e) { setErr(friendlyError(e)) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
      <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <MapPin size={14} /> Site of care scope
      </h4>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 10 }}>
        Limits what this account can see on top of its prescriber (NPI) grant.
      </div>
      {err && <div role="alert" style={{ fontSize: 12.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{err}</div>}
      {note && <div style={{ fontSize: 12.5, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>{note}</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {SCOPE_OPTIONS.map(o => (
          <label key={o.value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, cursor: canWrite ? 'pointer' : 'default' }}>
            <input type="radio" name={`soc-scope-${userId}`} value={o.value} checked={scope === o.value} disabled={!canWrite || busy}
              onChange={() => { setScope(o.value); setNote(null) }} style={{ marginTop: 3 }} />
            <span><span style={{ fontWeight: 600 }}>{o.label}</span><br /><span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{o.help}</span></span>
          </label>
        ))}
      </div>
      {canWrite && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => void save()} disabled={busy || scope === saved}
            style={{ fontSize: 12.5, padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary, #185FA5)', color: '#fff', fontWeight: 600, cursor: scope !== saved ? 'pointer' : 'default', opacity: scope !== saved ? 1 : .55 }}>
            {busy ? 'Saving…' : 'Save scope'}
          </button>
        </div>
      )}
    </div>
  )
}
